import { useEffect, useState } from "react";
import { api } from "./api";

/** SATU sumber pengambilan daftar gudang untuk form transaksi yang perlu tahu
 *  apakah kasir harus memilih gudang secara eksplisit (kontrak G1 keputusan
 *  owner #4: satu nota = satu gudang). Dipakai Sales.jsx & InputNota.jsx —
 *  disatukan di sini alih-alih disalin ke dua berkas supaya tidak berpisah
 *  jalan seperti riwayat `next_code`/`_next_code` (lihat CLAUDE.md).
 *
 * GET /inventory/warehouses digerbang izin `inventory_gudang.view` di
 * backend — beda dari /inventory/cabang-options (lib/cabang.js) yang cukup
 * login. Makanya pemanggilan HANYA dilakukan saat `diizinkan` true (dari
 * `can("inventory_gudang","view")` milik pemanggil), pola yang sama dengan
 * bolehLookupProduk/bolehLookupUnit di kedua halaman itu — kalau user tidak
 * berhak, endpoint akan 403; picker gudang sekadar tidak pernah muncul
 * (aturan proyek: tidak dicek = tidak ditampilkan), dan backend menurunkan
 * gudang otomatis kalau user hanya berhak atas satu gudang, atau menolak
 * dengan pesan jelas kalau ambigu (lihat kontrak G1 bagian C).
 */
export function useGudangOptions(diizinkan) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!diizinkan) { setItems([]); return; }
    let hidup = true;
    api.get("/inventory/warehouses").then(r => {
      if (hidup) setItems(r.data?.items || []);
    }).catch(() => {
      // Diam-diam nonaktif — pola yang sama dengan lookup produk/unit: tidak
      // berizin atau error jaringan berarti picker tidak muncul, bukan error
      // yang mengganggu form transaksi.
      if (hidup) setItems([]);
    });
    return () => { hidup = false; };
  }, [diizinkan]);
  return items;
}

/** True kalau BACKEND membatasi gudang yang boleh dipakai user ini — cermin
 *  PERSIS `warehouse_filter_for_user()` di
 *  `backend/routers/inventory/_shared.py` (dan `allowed_warehouse_ids()`
 *  yang memakainya): superadmin/admin/finance_ceo, atau siapa pun tanpa
 *  `allowed_branches`, TIDAK PERNAH dibatasi gudang sama sekali.
 *
 *  SATU sumber kebenaran dipakai baik oleh `wajibPilihGudang` (kapan
 *  `<select>` gudang wajib tampil) MAUPUN `pesanGudangButaArah` (kapan
 *  pesan 400 dari server perlu ditulis ulang) — supaya keduanya tidak
 *  pernah berpisah jalan seperti riwayat `next_code`/`_next_code`
 *  (lihat CLAUDE.md).
 */
export function userDibatasiGudang(user) {
  if (!user) return false;
  if (["superadmin", "admin", "finance_ceo"].includes(user.role)) return false;
  return (user.allowed_branches || []).length > 0;
}

/** Wajib pilih gudang di form HANYA kalau backend memang akan menolak
 *  tanpanya (kontrak G1-C / `_resolusi_warehouse_sale` di
 *  `backend/routers/sales.py`, dipakai ulang persis oleh `nota_module.py`):
 *  user dibatasi cabang (`userDibatasiGudang`) DAN daftar gudang yang boleh
 *  dipakainya berisi LEBIH dari satu (list 1 elemen -> backend menurunkan
 *  otomatis, poin 3 docstring `_resolusi_warehouse_sale`; list kosong ->
 *  bukan urusan picker, backend menolak dengan pesan "hubungi admin"
 *  tersendiri).
 *
 *  Versi lama cuma memeriksa `warehouses.length > 1`, yang SALAH untuk
 *  admin/superadmin/finance_ceo: `/inventory/warehouses` mengembalikan
 *  SEMUA gudang aktif untuk mereka (bukan gudang "milik" mereka), jadi
 *  daftarnya nyaris selalu >1 walau backend TIDAK PERNAH memaksa mereka
 *  memilih (poin 4 docstring yang sama) — FE mengunci tombol simpan atas
 *  alur yang backend sendiri terima.
 */
export function wajibPilihGudang(user, warehouses) {
  return userDibatasiGudang(user) && (warehouses || []).length > 1;
}

/** Backend bisa membalas 400 "Pilih gudang..." kapan pun ambigu terdeteksi
 *  DI SERVER (`_resolusi_warehouse_sale`) — TERMASUK saat form ini sendiri
 *  tidak (bisa) menampilkan pemilihnya, mis. kasir tidak berizin
 *  `inventory_gudang.view` sehingga `useGudangOptions` diam-diam kosong.
 *  Pesan asli jadi jalan buntu: menyuruh memilih sesuatu yang tak pernah ada
 *  di layar (larangan CLAUDE.md aturan 3 — tidak boleh ada aksi yang
 *  berujung 400/403 tanpa jalan keluar).
 *
 *  `gudangTampil` = nilai `wajibPilihGudang` (atau turunannya) yang dipakai
 *  form ini untuk merender `<select>` — kalau pemilihnya memang tampil,
 *  pesan asli backend sudah cukup actionable, balikan `null` supaya
 *  pemanggil pakai `pesanGagalDariServer` seperti biasa.
 */
export function pesanGudangButaArah(e, gudangTampil) {
  if (gudangTampil) return null;
  if (e?.response?.status !== 400) return null;
  const detail = e?.response?.data?.detail;
  const pesan = typeof detail === "string" ? detail : "";
  if (!/pilih gudang/i.test(pesan)) return null;
  return "Transaksi ini butuh gudang spesifik dan akun Anda berhak atas lebih dari satu, tapi "
    + "akun ini tidak punya izin melihat daftar gudang untuk memilihnya. Minta admin memberi "
    + "izin \"Gudang\" (inventory_gudang), atau minta akun ini dibatasi ke satu gudang saja "
    + "supaya tidak perlu memilih.";
}
